<template>
  <a-modal
    v-model:visible="visible"
    destroyOnClose
    centered
    :width="800"
    :mask="false"
    :maskClosable="false"
    :wrapClassName="'pointer-events-none preview-media-modal'"
    :wrap-style="{ overflow: 'hidden' }"
    :footer="null"
    @ok="handleOk"
  >
    <viewer :options="options" :images="props.mediaList" @inited="inited">
      <template #default="scope">
        <div style="height: 800px; overflow: hidden">
          <img style="opacity: 0" v-for="src in scope.images" :src="src" :key="src" />
        </div>
      </template>
    </viewer>
    <template #title>
      <div ref="modalTitleRef" style="width: 100%; cursor: move">
        <holder-outlined style="margin-right: 6px" />预览
      </div>
    </template>
    <template #modalRender="{ originVNode }">
      <div :style="transformStyle">
        <component :is="originVNode" />
      </div>
    </template>
  </a-modal>
</template>
<script lang="ts" setup>
import { ref, computed, CSSProperties, watch, watchEffect, Ref } from "vue";
import { useDraggable } from "@vueuse/core";
import { HolderOutlined } from "@ant-design/icons-vue";
type Props = {
  visible: boolean;
  mediaList: string[];
  mediaIndex: number;
};

type ViewerInstance = {
  index: number;
  image: HTMLImageElement;
};

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  mediaList: () => [],
  mediaIndex: 0,
});

const $viewer: Ref<ViewerInstance | null> = ref(null);

const options = {
  transition: false,
  inline: true,
  button: false,
  toolbar: {
    rotateLeft: true,
    prev: true,
    oneToOne: true,
    next: true,
    rotateRight: true,
  },
};

const inited = (viewer: any) => {
  $viewer.value = viewer;
  viewer.view(props.mediaIndex);
};

const emit = defineEmits<{
  (e: "update:visible", val: boolean): void;
}>();

const visible = computed({
  get() {
    return props.visible;
  },
  set(val) {
    emit("update:visible", val);
  },
});

const modalTitleRef = ref<HTMLElement | null>(null);
const { x, y, isDragging } = useDraggable(modalTitleRef);
const handleOk = (e: MouseEvent) => {
  emit("update:visible", false);
};
const startX = ref<number>(0);
const startY = ref<number>(0);
const startedDrag = ref(false);
const transformX = ref(0);
const transformY = ref(0);
const preTransformX = ref(0);
const preTransformY = ref(0);
const dragRect = ref({ left: 0, right: 0, top: 0, bottom: 0 });

watch([x, y], () => {
  if (!startedDrag.value) {
    startX.value = x.value;
    startY.value = y.value;
    const bodyRect = document.body.getBoundingClientRect();
    const titleRect = (modalTitleRef.value as HTMLElement).getBoundingClientRect();
    dragRect.value.right = bodyRect.width - titleRect.width;
    dragRect.value.bottom = bodyRect.height - titleRect.height;
    preTransformX.value = transformX.value;
    preTransformY.value = transformY.value;
  }
  startedDrag.value = true;
});

watch(isDragging, () => {
  if (!isDragging) {
    startedDrag.value = false;
  }
});

watchEffect(() => {
  if (startedDrag.value) {
    transformX.value =
      preTransformX.value +
      Math.min(Math.max(dragRect.value.left, x.value), dragRect.value.right) -
      startX.value;
    transformY.value =
      preTransformY.value +
      Math.min(Math.max(dragRect.value.top, y.value), dragRect.value.bottom) -
      startY.value;
  }
});
const transformStyle = computed<CSSProperties>(() => {
  return {
    transform: `translate(${transformX.value}px, ${transformY.value}px)`,
  };
});
</script>
<style lang="scss">
.preview-media-modal {
  .ant-modal-header {
    padding: 8px 12px;
  }
  .ant-modal-body {
    background-color: #a9a9a97d;
    padding: 0;
    max-height: 85vh;
  }
  .ant-modal-close-x {
    height: 39px;
    width: 36px;
    line-height: 39px;
  }
}
</style>
